import { Radio } from 'lucide-react';
import { useYouTubeLive } from '../lib/api/youtube';

interface LiveBadgeProps {
  programmeId: string;
  /** Show concurrent viewer count next to the badge when present. */
  showViewers?: boolean;
  className?: string;
}

/**
 * Pulsing red badge when a programme is on YouTube live, muted "upcoming" when
 * a stream is scheduled but hasn't started, and nothing at all when off-air or
 * the programme has no YouTube source configured. Polls every 30s via the
 * useYouTubeLive hook (server caches under the hood, so this is cheap).
 */
export function LiveBadge({ programmeId, showViewers = true, className = '' }: LiveBadgeProps) {
  const { data, isLoading } = useYouTubeLive(programmeId);
  if (isLoading || !data || !data.configured) return null;
  if (data.state === 'none') return null;

  if (data.state === 'upcoming') {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-900 ${className}`}
        title={
          data.scheduledStartTime
            ? `Scheduled for ${new Date(data.scheduledStartTime).toLocaleString()}`
            : 'Scheduled live broadcast'
        }
      >
        <Radio className="h-3 w-3" />
        Upcoming
      </span>
    );
  }

  // state === 'live'
  return (
    <span
      className={`inline-flex items-center gap-1 rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white ${className}`}
      title={data.title ?? 'Live on YouTube'}
    >
      <span className="relative flex h-1.5 w-1.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
      </span>
      Live
      {showViewers && data.concurrentViewers ? (
        <span className="ml-1 font-normal opacity-90">
          · {data.concurrentViewers.toLocaleString()}
        </span>
      ) : null}
    </span>
  );
}
